import { useEffect, useState } from "react";

import { getConfig } from "../../services/configService";
import { TOKEN } from "../../constants";
import {
  adoptSerializedUser,
  adoptStoredUser,
  completeSigninCallback,
  configure as configureTokenManager,
  serializeUser,
  signinRedirect,
  subscribe as subscribeTokenManager,
} from "../../services/tokenManager";
import { initSessionBridge, requestSession } from "../../services/sessionBridge";

const REDIRECT_GUARD_KEY = "wb.auth.redirects";

const PERMISSIONS_CACHE_KEY = "wb.permissions.cache";
const PERMISSIONS_TTL_MS = 10 * 60_000;

/** Current query params in the `%24%24` OAuth-state encoding (legacy implicit
 *  flow only — the code flow carries state via the oidc library instead). */
function encodeStateFromLocation(): string {
  let page_parameters_array = window.location.href.match(/\?(.*)/);
  let page_parameters = "";
  if (page_parameters_array && page_parameters_array.length > 1) {
    page_parameters = page_parameters_array[1].replace(/&/g, "%24%24");
    page_parameters = page_parameters.replace("#", "");
  }
  return page_parameters + "%24%24%24";
}

/** Current query string (auth params stripped) — the code flow's state payload. */
function statePayloadFromLocation(): string {
  const params = new URLSearchParams(window.location.search);
  ["code", "state", "id_token", "access_token", "session_state"].forEach((k) =>
    params.delete(k),
  );
  return params.toString();
}

/** True = proceed; false = third login redirect inside 60s (a loop) — stay put. */
function redirectLoopGuard(): boolean {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(REDIRECT_GUARD_KEY);
    let count = 0;
    let start = now;
    if (raw) {
      const [c, s] = raw.split(":");
      count = Number(c) || 0;
      start = Number(s) || now;
    }
    if (now - start > 60_000) {
      count = 0;
      start = now;
    }
    if (count >= 2) return false;
    sessionStorage.setItem(REDIRECT_GUARD_KEY, `${count + 1}:${start}`);
    return true;
  } catch {
    return true;
  }
}

function legacyRedirect(): void {
  const config = getConfig();
  try {
    window.location.href =
      config.redirect_url + "&state=" + encodeStateFromLocation();
  } catch (err: any) {
    window.alert("Error:" + err.message);
  }
}

// First caller wins; navigation tears the page down so no reset is needed.
let redirectInFlight = false;

/** Bounce through SSO carrying the query params as OAuth state (PageContext
 *  restores them post-login). Code flow (oidc-client-ts, PKCE) when
 *  `auth_flow === 'code'`, legacy implicit otherwise. */
export function redirectToLogin(): void {
  if (redirectInFlight) return;
  if (!redirectLoopGuard()) {
    console.error("auth: login redirect loop detected — staying on page");
    return;
  }
  redirectInFlight = true;
  if (getConfig().auth_flow === "code") {
    void signinRedirect(statePayloadFromLocation()).catch((err) => {
      console.error("auth: code-flow redirect failed; using legacy login", err);
      legacyRedirect();
    });
    return;
  }
  legacyRedirect();
}

function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Epoch-ms expiry of the session JWT (`exp` claim). Null when undecodable or
 * on local dev (the localhost token is treated as non-expiring for gating).
 */
export function sessionExpiresAtMs(token: string | null): number | null {
  if (!token || window.location.hostname.includes("localhost")) return null;
  const exp = decodeJwtClaims(token)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

/* ──────────────── Boot (runs once per page load) ──────────────── */

interface BootResult {
  token: string | null;
  stateParams: string | null;
  /** Pre-permissions notice/failure to surface (e.g. while redirecting). */
  errorText: string | null;
}

let bootPromise: Promise<BootResult> | null = null;

/** Legacy implicit-flow fragment: `#id_token=...`. */
function extractLegacyToken(): string | null {
  const m = /[?&#]id_token=([^&#]*)/.exec(window.location.href);
  return m ? m[1] : null;
}

/** Legacy state parser — operates on the raw (still-encoded) href. */
function extractLegacyStateParams(): string | null {
  const url = window.location.href;
  const stateParamStart = "state=";
  const stateParamEnd = "%24%24%24";

  if (url.includes(stateParamStart)) {
    const startIndex = url.indexOf(stateParamStart);
    const endIndex = url.indexOf(stateParamEnd);
    let state_variable = url.substring(
      startIndex + stateParamStart.length, endIndex
    );
    state_variable = state_variable.replace(/%24%24/g, "&");
    state_variable = state_variable.replace(/%3D/g, "=");
    return state_variable;
  }
  return null;
}

/** Drop just-consumed auth params immediately — the single-use code must not
 *  linger in the URL (a remount or back-nav would try to redeem it again). */
function scrubAuthParams(keys: readonly string[]): void {
  const url = new URL(window.location.href);
  keys.forEach((k) => url.searchParams.delete(k));
  window.history.replaceState({}, "", url.toString());
}

async function doBoot(): Promise<BootResult> {
  // Local dev: fixed token, no SSO round-trip.
  if (window.location.hostname.includes("localhost")) {
    return {
      token: TOKEN,
      stateParams: extractLegacyStateParams(),
      errorText: null,
    };
  }
  const config = getConfig();
  configureTokenManager({
    authDomain: config.auth_domain,
    issuer: config.oidc_issuer,
    clientId: config.client_id,
    redirectUri: config.oauth_redirect_uri,
    exposeDebugHandle: config.name !== "prod",
  });
  // Donate this tab's session to other booting tabs once we hold one.
  initSessionBridge(serializeUser);

  // Code-flow callback: the library redeems the single-use code (PKCE,
  // state validation) and persists the session in this tab.
  const search = new URLSearchParams(window.location.search);
  if (search.get("code")) {
    try {
      const { idToken, stateParams } = await completeSigninCallback();
      scrubAuthParams(["code", "state"]);
      return { token: idToken, stateParams, errorText: null };
    } catch (err) {
      console.error("auth: code exchange failed", err);
      scrubAuthParams(["code", "state"]);
      redirectToLogin();
      return {
        token: null,
        stateParams: null,
        errorText: "Sign-in could not complete — retrying login...",
      };
    }
  }

  // Same-tab survivor (F5 / duplicated tab): session persisted per-tab.
  const stored = await adoptStoredUser();
  if (stored) {
    return { token: stored, stateParams: null, errorText: null };
  }

  // Legacy implicit fragment (rollback path while the code flow rolls out).
  const legacyToken = extractLegacyToken();
  if (legacyToken) {
    return {
      token: legacyToken,
      stateParams: extractLegacyStateParams(),
      errorText: null,
    };
  }

  // New tab while the app is open elsewhere: adopt that tab's session.
  if (config.auth_flow === "code") {
    const donated = await requestSession();
    if (donated) {
      const adopted = await adoptSerializedUser(donated);
      if (adopted) {
        return { token: adopted, stateParams: null, errorText: null };
      }
    }
  }

  // No credentials at all — go log in.
  redirectToLogin();
  return { token: null, stateParams: null, errorText: "Initiating SSO login..." };
}

/** Single-flight boot — StrictMode double-fires effects and the authorization
 *  code is single-use, so every caller shares one promise. */
function bootOnce(): Promise<BootResult> {
  if (bootPromise === null) bootPromise = doBoot();
  return bootPromise;
}

/* ──────────────── Permissions (cached per session) ──────────────── */

function readCachedPermissions(sub: string): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sub?: string; at?: number; data?: unknown };
    if (parsed.sub !== sub || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > PERMISSIONS_TTL_MS) return null;
    return (parsed.data as Record<string, unknown>) ?? null;
  } catch {
    return null;
  }
}

function writeCachedPermissions(sub: string, data: unknown): void {
  try {
    sessionStorage.setItem(
      PERMISSIONS_CACHE_KEY,
      JSON.stringify({ sub, at: Date.now(), data }),
    );
  } catch {
    /* storage full/blocked — cache is best-effort */
  }
}

export const useAuthContextUtils = () => {
  const config = getConfig();
  const [token, setToken] = useState<string | null>(null);
  const [stateParams, setStateParams] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<{} | null>(null);

  // Silent renewals flow into context state so useAuth().token consumers
  // (Token page, FDTR expiry fallback) stay current (~1 re-render/hour).
  useEffect(
    () =>
      subscribeTokenManager((s) => {
        if (s.idToken) setToken(s.idToken);
      }),
    [],
  );

  /**********************
   * Verify token with the API (sets userData / error, ends loading).
   * The app-level permission check is cached per user for 10 min so a
   * reload doesn't pay the Lambda round-trip; the API still enforces the
   * JWT on every call.
   */
  const verifyPermissions = (idToken: string): void => {
    const sub = decodeJwtClaims(idToken)?.sub;
    const cached = typeof sub === "string" ? readCachedPermissions(sub) : null;
    if (cached) {
      setUserData(cached);
      setLoading(false);
      return;
    }

    const myHeaders = new Headers({
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-WBAPI-JWT": `${idToken}`,
    });

    fetch(`${config.apiEndpoint}/userPermissions`, {
      method: "GET",
      headers: myHeaders,
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Failed to fetch user permissions");
        }
        return response.json();
      })
      .then(data => {
        const permissions = data?.data?.permissions;
        // Check if permissions are available
        if (!permissions) {
          setError("No access: permissions not found.");
        } else {
          setUserData(data.data);
          if (typeof sub === "string") writeCachedPermissions(sub, data.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to authenticate with the provided token.");
        setLoading(false);
      });
  };

  /********************
   * Authenticate User
   */
  const authenticateUser = () => {
    void bootOnce().then((boot) => {
      if (boot.stateParams) setStateParams(boot.stateParams);
      if (boot.token) {
        // Token set before loading flips — RootPage's gate guarantees
        // ApiClient consumers never mount with a null client.
        setToken(boot.token);
        verifyPermissions(boot.token);
      } else {
        if (boot.errorText) setError(boot.errorText);
        setLoading(false);
      }
    });
  };

  return {
    authenticateUser,
    stateParams,
    userData,
    loading,
    token,
    error
  }
}
